/** About this record: its address, where the data comes from, its validity and the shape that drew the page. */

import { useTranslation } from 'react-i18next';
import { CopyButton } from '@bn-apps/dim-ui-react';
import { pickLang, type ViewNodeShape } from '@utils/viewShapes.utils';
import type { Validity } from '../page';
import { formatDate } from '../widgets/values';
import styles from '../EntityTemplate.module.scss';

interface AboutProps {
  uri: string;
  validity: Validity;
  nodeShapes: ViewNodeShape[];
  /** The location type of the drawing at the top: spot, linear or area. */
  kind: 'spot' | 'linear' | 'area' | null;
  language: string;
}

const About = ({ uri, validity, nodeShapes, language, kind }: AboutProps): JSX.Element => {
  const { t, i18n } = useTranslation();
  const date = (value: string) => formatDate(value, i18n.language);
  const valid = validity.from
    ? validity.to
      ? t('entity.template.about.validRange', { from: date(validity.from), to: date(validity.to) })
      : t('entity.template.about.validSince', { from: date(validity.from) })
    : t('entity.template.status.unknown');
  const shape = nodeShapes[0] ? (pickLang(nodeShapes[0].label, language) ?? nodeShapes[0].id) : '';
  return (
    <section className={styles.about} aria-labelledby="entity-about">
      <h2 id="entity-about" className={styles.visuallyHidden}>
        {t('entity.template.about.title')}
      </h2>
      <div>
        <div className={styles.aboutLabel}>{t('entity.template.about.address')}</div>
        <div className={styles.address}>
          <code>{uri.replace(/^https?:\/\//, '')}</code>
          <CopyButton text={uri} label={t('entity.template.about.copy')} copiedLabel={t('entity.template.about.copied')} size="xs" hideText variant="tertiary" />
        </div>
      </div>
      <div>
        <div className={styles.aboutLabel}>{t('entity.template.about.valid')}</div>
        <div>{valid}</div>
      </div>
      <div>
        <div className={styles.aboutLabel}>{t('entity.template.about.drawnBy')}</div>
        <div>{kind ? t('entity.template.about.shape', { shape, kind: t(`entity.template.location.${kind}`) }) : t('entity.template.about.shapeOnly', { shape })}</div>
      </div>
    </section>
  );
};

export default About;
